<script setup lang="ts">
/**
 * Page V2 « Collaborateurs » — inventaire consolidé du matériel détenu par
 * chaque personne. Données dérivées du store onboarding (agrégation des dossiers
 * + tickets de remplacement). Recherche locale réactive + navigation vers la
 * fiche /onboarding/collaborateurs/:userUuid.
 * Ton visuel aligné sur OnboardingListPage.vue (cards, table, lignes cliquables).
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CubeIcon,
  PlusIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import { isReplacementActive } from '@/v2/onboarding/catalog'
import type { CollaboratorView } from '@/v2/onboarding/types'
import type { ApiCollaboratorIdentity } from '@/api/materialApi'
import OnbNewCollaboratorDialog from '@/v2/components/onboarding/OnbNewCollaboratorDialog.vue'
import OnbAssignAssetDialog from '@/v2/components/onboarding/OnbAssignAssetDialog.vue'

const router = useRouter()
const store = useOnboardingStore()
const { collaborators } = storeToRefs(store)

onMounted(() => store.ensureLoaded())

// ── Recherche locale (nom / prénom / email / entité) ──────────────────────────
const query = ref('')

const filtered = computed<CollaboratorView[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return collaborators.value
  return collaborators.value.filter((c) =>
    [c.firstName, c.lastName, c.email, c.entity]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q)),
  )
})

// ── Helpers ────────────────────────────────────────────────────────────────────
function fullName(c: CollaboratorView): string {
  return `${c.firstName} ${c.lastName}`.trim()
}

/** Nombre de remplacements actifs (ni terminés ni annulés). */
function activeReplacements(c: CollaboratorView): number {
  return c.tickets.filter((t) => isReplacementActive(t.status)).length
}

function detailPath(c: CollaboratorView): string {
  return `/onboarding/collaborateurs/${encodeURIComponent(c.userUuid ?? '')}`
}

function goTo(c: CollaboratorView) {
  router.push(detailPath(c))
}

// ── Ajout d'un collaborateur (sélection d'un User puis affectation de matériel) ─
const newCollabDialog = ref<InstanceType<typeof OnbNewCollaboratorDialog> | null>(null)
const assignDialog = ref<InstanceType<typeof OnbAssignAssetDialog> | null>(null)

/** Confirmation simple après affectation (le store recharge la liste lui-même). */
const assignedNotice = ref('')

function openNewCollaborator() {
  newCollabDialog.value?.open()
}

/** Un User a été choisi → on ouvre la modale d'affectation de matériel. */
function onUserSelected(user: ApiCollaboratorIdentity) {
  assignDialog.value?.open(user.userUuid)
}

function onAssigned(count: number) {
  assignedNotice.value =
    count > 1
      ? `${count} équipements affectés au collaborateur.`
      : 'Équipement affecté au collaborateur.'
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <!-- ── En-tête ──────────────────────────────────────────────────────────── -->
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="flex items-center gap-2 text-2xl font-bold">
          <UsersIcon class="h-6 w-6 text-primary" aria-hidden="true" />
          Collaborateurs
        </h1>
        <p class="mt-1 max-w-prose text-base-content/80">
          Inventaire du matériel détenu par chaque collaborateur.
        </p>
      </div>
      <div class="flex w-full flex-wrap gap-2 sm:w-auto">
        <RouterLink to="/onboarding" class="btn btn-ghost w-full sm:w-auto">
          <ArrowLeftIcon class="h-5 w-5" aria-hidden="true" />
          Dossiers
        </RouterLink>
        <button type="button" class="btn btn-primary w-full sm:w-auto" @click="openNewCollaborator">
          <PlusIcon class="h-5 w-5" aria-hidden="true" />
          Ajouter un collaborateur
        </button>
      </div>
    </header>

    <!-- ── Recherche ────────────────────────────────────────────────────────── -->
    <section class="mt-6" aria-labelledby="search-title">
      <h2 id="search-title" class="sr-only">Rechercher un collaborateur</h2>
      <label class="form-control w-full max-w-md">
        <span class="sr-only">Rechercher par nom, email ou entité</span>
        <input
          v-model="query"
          type="search"
          class="input input-bordered w-full"
          placeholder="Rechercher (nom, prénom, email, entité)…"
          aria-label="Rechercher par nom, prénom, email ou entité"
        />
      </label>
    </section>

    <!-- ── Tableau des collaborateurs ───────────────────────────────────────── -->
    <section class="mt-4" aria-labelledby="collaborators-title">
      <h2 id="collaborators-title" class="sr-only">Liste des collaborateurs</h2>
      <div class="hidden overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm md:block">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Nom</th>
              <th scope="col" class="hidden md:table-cell">Poste</th>
              <th scope="col" class="hidden md:table-cell">Entité</th>
              <th scope="col">Matériel détenu</th>
              <th scope="col">Remplacements en cours</th>
            </tr>
          </thead>
          <tbody>
            <!-- État vide -->
            <tr v-if="!filtered.length">
              <td colspan="5" class="py-12 text-center">
                <UsersIcon class="mx-auto h-10 w-10 text-base-content/40" aria-hidden="true" />
                <p class="mt-2 text-base-content/80">
                  {{ query ? 'Aucun collaborateur ne correspond à la recherche.' : 'Aucun collaborateur enregistré.' }}
                </p>
              </td>
            </tr>
            <!-- Lignes -->
            <tr
              v-for="c in filtered"
              v-else
              :key="c.userUuid ?? c.email"
              tabindex="0"
              class="cursor-pointer transition-colors hover motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              @click="goTo(c)"
              @keydown.enter.prevent="goTo(c)"
              @keydown.space.prevent="goTo(c)"
            >
              <td>
                <RouterLink
                  :to="detailPath(c)"
                  class="font-semibold text-primary hover:underline"
                  @click.stop
                >
                  {{ fullName(c) }}
                </RouterLink>
              </td>
              <td class="hidden md:table-cell">{{ c.jobTitle || '—' }}</td>
              <td class="hidden md:table-cell">{{ c.entity || '—' }}</td>
              <td>
                <span class="badge badge-ghost gap-1 tabular-nums">
                  <CubeIcon class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ c.ownedAssets.length }}
                </span>
              </td>
              <td>
                <span
                  v-if="activeReplacements(c) > 0"
                  class="badge badge-warning gap-1 tabular-nums"
                >
                  <ArrowPathIcon class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ activeReplacements(c) }}
                </span>
                <span v-else class="text-base-content/80" aria-hidden="true">—</span>
                <span v-if="activeReplacements(c) === 0" class="sr-only">Aucun</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Cartes (mobile) ──────────────────────────────────────────────── -->
      <ul role="list" class="grid grid-cols-1 gap-3 md:hidden">
        <!-- État vide -->
        <li
          v-if="!filtered.length"
          class="card border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body items-center gap-3 py-12 text-center">
            <UsersIcon class="h-10 w-10 text-base-content/40" aria-hidden="true" />
            <p class="text-base-content/80">
              {{ query ? 'Aucun collaborateur ne correspond à la recherche.' : 'Aucun collaborateur enregistré.' }}
            </p>
          </div>
        </li>
        <!-- Cartes -->
        <li v-for="c in filtered" v-else :key="c.userUuid ?? c.email">
          <RouterLink
            :to="detailPath(c)"
            class="card block border border-base-300 bg-base-100 shadow-sm transition-shadow hover:shadow-md motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <div class="card-body gap-2 p-4">
              <div class="flex items-start justify-between gap-2">
                <span class="font-semibold break-words">{{ fullName(c) }}</span>
                <span class="badge badge-ghost gap-1 tabular-nums">
                  <CubeIcon class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ c.ownedAssets.length }}
                </span>
              </div>
              <p class="text-sm break-words text-base-content/80">
                {{ c.jobTitle || '—' }} · {{ c.entity || '—' }}
              </p>
              <span
                v-if="activeReplacements(c) > 0"
                class="badge badge-warning gap-1 tabular-nums"
              >
                <ArrowPathIcon class="h-3.5 w-3.5" aria-hidden="true" />
                {{ activeReplacements(c) }} remplacement(s) en cours
              </span>
            </div>
          </RouterLink>
        </li>
      </ul>
    </section>

    <!-- Confirmation après affectation -->
    <div
      v-if="assignedNotice"
      role="status"
      aria-live="polite"
      class="toast toast-end z-50"
    >
      <div class="alert alert-success">
        <span>{{ assignedNotice }}</span>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          @click="assignedNotice = ''"
        >
          Fermer
        </button>
      </div>
    </div>

    <!-- Sélection d'un collaborateur (User existant) puis affectation de matériel -->
    <OnbNewCollaboratorDialog ref="newCollabDialog" @selected="onUserSelected" />
    <OnbAssignAssetDialog ref="assignDialog" @assigned="onAssigned" />
  </div>
</template>
